/**
 * Copyright 2020 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import Head from 'next/head';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes, currencyLocales } from '@/constants/locale';
import { useProductSkuDeals } from '@/features/product/api/getProductDeals';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { getPathnameFromAsPath } from '@/utils/urlHelpers';
import { useFeatureFlag } from '@/features/globalConfig';
import type { SchemaOffers, UnitPriceSpecificationSchema } from '@/types/seoSchema';
import { getBaseSchemaUrl } from '@/utils/common';
import { getCountryFromISO3166 } from '@/utils/getCountryFromLocale';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { convertDate } from '@/features/shelf/utils/convertDate';
import dayjs from '@/lib/dayjs';
import { metaTagKey } from '@/constants/seoMetaTags';

type Schema = {
  '@context': string;
  '@type': string;
  '@id': string;
  name: string;
  image: string;
  description: string;
  mpn: string;
  sku: string;
  brand: { '@type': 'Brand'; name: string };
  offers?: SchemaOffers;
};

export const SEOProductSchema = () => {
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';

  const { data: productDetailsData, isSuccess: productDetailsSuccess } = useProductDetails();
  const skuIds =
    productDetailsSuccess && productDetailsData?.product?.itemId
      ? [productDetailsData.product.itemId]
      : null;

  const { data: skuDetailsData } = useProductSkuDetails({
    skuIds,
  });
  const { data: skuDealsData } = useProductSkuDeals({
    skuIds,
    enabled: isNewDealsApiEnabled,
  });
  const locale = useLocale();
  const router = useLegacyRouter();

  const productSchema = (() => {
    if (!productDetailsData) {
      return null;
    }

    const productDetails = productDetailsData.product;
    const skuDetails = skuDetailsData?.[0] ?? null;
    const skuPricingAndAvailability = skuDetails?.skuPricingAndAvailability;
    const retailPrice = skuPricingAndAvailability?.retailPrice || 0;
    const corePrice = skuPricingAndAvailability?.corePrice || 0;
    const price = Number(retailPrice + corePrice).toFixed(2);
    const isProductAvailable =
      skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions?.some(
        (option) => option.availableQty > 0,
      );
    const homeDeliveryOption =
      skuPricingAndAvailability?.skuAvailabilityInfo?.fulfillmentOptions?.find(
        (option) => option.fulfillmentTypeId === FULFILLMENT_METHODS.ONLINEORDER,
      );
    const deliveryTime = homeDeliveryOption?.estimatedDeliveryDate
      ? convertDate(dayjs(), homeDeliveryOption?.estimatedDeliveryDate, locale)
      : null;

    const discountedPrice = isNewDealsApiEnabled
      ? skuDealsData?.[0]?.skuDealsInfo?.discountedPrice
      : skuDetails?.skuPricingAndAvailability?.discountedPrice;

    const {
      subBrandName,
      marketingCopy,
      featuresAndBenefits,
      partNumber,
      itemDescription,
      itemId,
      itemImageUrl,
    } = productDetails ?? {};

    const baseUrl = getBaseSchemaUrl(locale);

    let productDescription = '';

    if (marketingCopy) {
      productDescription = marketingCopy;
    } else if (featuresAndBenefits?.length) {
      productDescription = featuresAndBenefits.join(' ');
    }

    const schema: Schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${baseUrl}${getPathnameFromAsPath(router.asPath)}`,
      name: itemDescription ?? '',
      image: itemImageUrl ?? '',
      description: productDescription,
      mpn: partNumber ?? '',
      sku: itemId ?? '',
      brand: { '@type': 'Brand', name: subBrandName ?? '' },
    };

    const priceSpecification: UnitPriceSpecificationSchema[] = [];

    priceSpecification.push({
      '@type': 'UnitPriceSpecification',
      price,
      priceCurrency: currencyLocales[locale],
      priceType: discountedPrice
        ? 'https://schema.org/StrikethroughPrice'
        : 'https://schema.org/ListPrice',
    });

    schema.offers = {
      '@type': 'Offer',
      url: `${baseUrl}${getPathnameFromAsPath(router.asPath)}`,
      availability: isProductAvailable
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      price: discountedPrice || price,
      priceCurrency: currencyLocales[locale],
      priceSpecification,
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        returnPolicyDays: 30,
        merchantReturnDays: 90,
        applicableCountry: getCountryFromISO3166(locale),
        returnMethod: 'https://schema.org/ReturnInStore',
        returnFees: 'https://schema.org/FreeReturn',
        refundType: 'https://schema.org/FullRefund',
      },
    };
    if (schema.offers && locale === countryCodes.us && deliveryTime) {
      schema.offers.shippingDetails = {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: getCountryFromISO3166(locale),
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          transitTime: deliveryTime,
        },
      };
    }

    return JSON.stringify(schema);
  })();

  if (!productSchema) {
    return null;
  }

  return (
    <Head>
      <script
        type="application/ld+json"
        key={metaTagKey('productSchema')}
        dangerouslySetInnerHTML={{
          __html: productSchema,
        }}
      />
    </Head>
  );
};
